import { Link } from "react-router-dom";
import { ArrowRight, Compass, Layers, Users } from "lucide-react";

export default function About() {
  return (
    <div className="bg-transparent">
      {/* Hero */}
      <section className="border-b border-[#EFEFEF] bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-12 items-end">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E]">◇ The Collective</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] mt-5">
              We make<br />moodboards<br /><span className="italic">together.</span>
            </h1>
            <p className="font-body text-[#595959] text-base sm:text-lg mt-7 max-w-xl leading-relaxed">
              VoraStyle is a creative curation collective in the spirit of the great visual journals of the last decade — a place to clip beautiful things from anywhere on the web, compose them into sets on a drag-and-drop canvas, and share them with curators who actually look back.
            </p>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#333333] leading-relaxed border-l-2 border-[#9CA47E] pl-5">
            <div className="text-[#9CA47E] mb-3">§ Our promise</div>
            Open community.<br />
            No paywalls on the canvas.<br />
            Credit goes to the curator.<br />
            The eye comes first.
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1200px] mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          {
            icon: Compass,
            title: "Curation as craft",
            body: "A moodboard is an argument. Composition, color, juxtaposition — that's the work. We build tools that respect it: precise cutouts, manual edits, free-form canvas, attribution that travels with every piece.",
          },
          {
            icon: Layers,
            title: "Open library",
            body: "Every clip you save makes the next curator's set easier. The Archive Tool extension and product library are deliberately frictionless — what you find, the collective inherits.",
          },
          {
            icon: Users,
            title: "Credit, always",
            body: "When another curator reuses your edited cutout, it carries your handle on the canvas. Visibility for the people doing the careful work behind the scenes.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="bg-white border border-[#EFEFEF] p-6">
            <div className="w-10 h-10 bg-[#333333] text-white flex items-center justify-center mb-4">
              <Icon className="w-4 h-4" />
            </div>
            <div className="font-heading text-2xl tracking-tight">{title}</div>
            <p className="font-body text-sm text-[#595959] leading-relaxed mt-2">{body}</p>
          </div>
        ))}
      </section>

      {/* Story */}
      <section className="border-t border-[#EFEFEF] bg-white">
        <div className="max-w-[920px] mx-auto px-6 py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">§ The story</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">A quiet love letter to the visual journal.</h2>
          <div className="font-body text-base text-[#333333] leading-relaxed mt-7 space-y-5 max-w-[68ch]">
            <p>
              There was a generation of stylists, editors, interior obsessives and weekend curators who built their eye in the open — clipping, composing, swapping, watching. When the great visual-journal platforms quietly disappeared, the everyday ritual went with them: clip a thing, drop it on a board, see what someone else did with the same thing yesterday.
            </p>
            <p>
              VoraStyle is a creative curation collective in its own form — bring your eye, share your edits, find your people.
            </p>
            <p>
              We're a tiny team building this in the open. The roadmap is shaped by the curators who use it. If you have an opinion, we want to hear it.
            </p>
          </div>
        </div>
      </section>

      {/* Beliefs */}
      <section className="max-w-[1200px] mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">§ What we believe</div>
          <h3 className="font-heading text-3xl sm:text-4xl tracking-tight leading-[1.05]">Tools that disappear. Communities that don't.</h3>
        </div>
        <ul className="font-body text-[#333333] text-base leading-relaxed space-y-4">
          {[
            "A moodboard tool should never get in the way of the eye.",
            "Curators deserve credit by default, not as a bolt-on.",
            "AI is a brush, not a shortcut. Use it where it earns its place.",
            "Free where we can, paid where it sustains the work — never confused.",
          ].map((b) => (
            <li key={b} className="flex gap-3">
              <span className="text-[#9CA47E] mt-1">◇</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="bg-[#333333] text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-10 items-end">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">◇ Join in</div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1]">
              Open the canvas.<br /><span className="italic">Make something.</span>
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/register" data-testid="about-cta-register" className="px-5 py-4 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[12px] uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2">
              Join the collective <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/discover" data-testid="about-cta-discover" className="px-5 py-4 bg-transparent border border-white text-white hover:bg-white hover:text-[#333333] font-mono text-[12px] uppercase tracking-[0.18em] text-center transition-colors">
              Browse curators' sets
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
